import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings, 
  Building2, 
  User, 
  Lock, 
  Save,
  Loader2,
  Upload,
  Image
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { api, user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [company, setCompany] = useState(null);
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name,
        email: user.email
      });
      
      // Buscar dados da empresa do usuário
      if (user.company_id) {
        fetchCompanyData();
      }
    }
  }, [user]);

  const fetchCompanyData = async () => {
    try {
      const response = await api.get(`/companies/${user.company_id}`);
      setCompany(response.data);
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    // Validar tamanho (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/companies/${user.company_id}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Adiciona timestamp para evitar cache
      const newLogoUrl = response.data.logo_url + '?t=' + Date.now();
      
      // Atualiza o estado local imediatamente
      setCompany(prev => ({ ...prev, logo_url: newLogoUrl }));
      
      toast.success('Logo atualizado com sucesso! A página será recarregada...');
      
      // Pequeno delay para mostrar o toast antes de recarregar
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer upload do logo');
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await api.put(`/users/${user.id}`, {
        ...profileData,
        password: '',
        role: user.role,
        company_id: user.company_id
      });
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (passwordData.new_password.length < 3) {
      toast.error('A nova senha deve ter pelo menos 3 caracteres');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/users/${user.id}`, {
        name: user.name,
        email: user.email,
        password: passwordData.new_password,
        role: user.role,
        company_id: user.company_id
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-[#1a365d]" data-testid="settings-title">
            <Settings className="inline mr-3" size={32} />
            Configurações
          </h1>
          <p className="text-slate-500 mt-1">
            Gerencie as configurações do sistema e do seu perfil
          </p>
        </div>

        <div className="max-w-3xl">
          <Tabs defaultValue="company" className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="company" className="data-[state=active]:bg-[#1a365d] data-[state=active]:text-white">
                <Building2 className="mr-2" size={16} />
                Empresa
              </TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-[#1a365d] data-[state=active]:text-white">
                <User className="mr-2" size={16} />
                Perfil
              </TabsTrigger>
              <TabsTrigger value="security" className="data-[state=active]:bg-[#1a365d] data-[state=active]:text-white">
                <Lock className="mr-2" size={16} />
                Segurança
              </TabsTrigger>
            </TabsList>

            {/* Company Settings */}
            <TabsContent value="company">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">
                    Dados da Empresa
                  </h2>
                  <p className="text-slate-500 text-sm mb-6">
                    Visualize os dados da sua empresa e personalize o logo
                  </p>
                </div>

                {!user?.company_id ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building2 className="mx-auto mb-4 text-slate-300" size={48} />
                    <p>Você não está vinculado a nenhuma empresa.</p>
                    <p className="text-sm mt-2">Entre em contato com o administrador.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Nome da Empresa - Somente Leitura */}
                    <div>
                      <Label htmlFor="company_name">Nome da Empresa</Label>
                      <Input
                        id="company_name"
                        value={company?.name || 'Carregando...'}
                        disabled
                        className="mt-1 bg-slate-50 cursor-not-allowed"
                        data-testid="company-name-setting"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        O nome da empresa não pode ser alterado aqui. Contate o administrador do sistema.
                      </p>
                    </div>

                    {/* CNPJ - Somente Leitura */}
                    {company?.cnpj && (
                      <div>
                        <Label htmlFor="company_cnpj">CNPJ</Label>
                        <Input
                          id="company_cnpj"
                          value={company?.cnpj || '-'}
                          disabled
                          className="mt-1 bg-slate-50 cursor-not-allowed font-mono"
                        />
                      </div>
                    )}

                    {/* Upload de Logo */}
                    <div>
                      <Label>Logo da Empresa</Label>
                      <div className="mt-2 flex items-start gap-6">
                        {/* Preview do Logo */}
                        <div className="flex-shrink-0">
                          <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                            {company?.logo_url && (
                              <img 
                                src={company.logo_url.startsWith('/api') 
                                  ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}` 
                                  : company.logo_url
                                }
                                alt="Logo da empresa" 
                                className="max-w-full max-h-full object-contain p-2"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.parentElement?.querySelector('[data-logo-fallback]');
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            )}
                            <div
                              data-logo-fallback
                              className="text-center text-slate-400 items-center justify-center flex-col"
                              style={{ display: company?.logo_url ? 'none' : 'flex' }}
                            >
                              <Image size={32} className="mx-auto mb-2" />
                              <span className="text-xs">Sem logo</span>
                            </div>
                          </div>
                        </div>

                        {/* Botão de Upload */}
                        <div className="flex-1">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleLogoUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingLogo}
                            className="w-full sm:w-auto"
                          >
                            {uploadingLogo ? (
                              <>
                                <Loader2 className="mr-2 animate-spin" size={16} />
                                Enviando...
                              </>
                            ) : (
                              <>
                                <Upload className="mr-2" size={16} />
                                Fazer Upload do Logo
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-slate-500 mt-2">
                            Formatos aceitos: PNG, JPG, SVG. Tamanho máximo: 2MB.
                            <br />
                            Recomendado: imagem quadrada ou com fundo transparente.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Profile Settings */}
            <TabsContent value="profile">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">
                    Dados do Perfil
                  </h2>
                  <p className="text-slate-500 text-sm mb-6">
                    Atualize suas informações pessoais
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nome Completo</Label>
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      placeholder="Seu nome completo"
                      className="mt-1"
                      data-testid="profile-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      placeholder="seu@email.com"
                      className="mt-1"
                      data-testid="profile-email-input"
                    />
                  </div>

                  <Button 
                    onClick={handleSaveProfile} 
                    disabled={loading}
                    className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                    data-testid="save-profile-button"
                  >
                    {loading ? (
                      <Loader2 className="mr-2 animate-spin" size={16} />
                    ) : (
                      <Save className="mr-2" size={16} />
                    )}
                    Salvar Alterações
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Security Settings */}
            <TabsContent value="security">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">
                    Alterar Senha
                  </h2>
                  <p className="text-slate-500 text-sm mb-6">
                    Atualize sua senha de acesso ao sistema
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new_password">Nova Senha</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      placeholder="Digite a nova senha"
                      className="mt-1"
                      data-testid="new-password-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirmar Nova Senha</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      placeholder="Confirme a nova senha"
                      className="mt-1"
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button 
                    onClick={handleChangePassword} 
                    disabled={loading || !passwordData.new_password || !passwordData.confirm_password}
                    className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                    data-testid="change-password-button"
                  >
                    {loading ? (
                      <Loader2 className="mr-2 animate-spin" size={16} />
                    ) : (
                      <Lock className="mr-2" size={16} />
                    )}
                    Alterar Senha
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
